import { useAuth0 } from '@auth0/auth0-react';
import {
  Avatar,
  Button,
  Card,
  Col,
  CreateButton,
  Form,
  Icons,
  Input,
  Row,
  Show,
  Space,
  Switch,
  Typography,
  Upload,
  getValueFromEvent,
  notification,
} from '@pankod/refine-antd';
import { file2Base64 } from '@pankod/refine-core';
import { PaymentMethodsTable } from 'components';
import { useUserData } from 'hooks';
import { useEffect, useState } from 'react';
import { getAppInstances } from 'services/app-instance';
import { getAllCompanyConfig, getCompanyInfo, updateCompanyInfo } from 'services/config';
import { getErpOption, setErpOption } from 'services/erp-options';
import { VatRateSettingCard } from 'components/vat-rate/VatRateSettingCard';
import { InvoiceProfilesSettingCard } from 'components/invoice-profile/InvoiceProfilesSettingCard';
import { useFeatureFlag } from 'hooks/useFeatureFlags';
import { getOrganizations } from 'services/organization';
import useAppInstanceStore from 'store/useAppInstanceStore';
import useOrganizationStore from 'store/useOrganizationStore';
import {
  beforeImageUploadValidation,
  isJpgOrPngFile,
  isLessThanSize,
  MAX_UPLOAD_FILE_SIZE_MB,
} from 'utilities/file';
import MetaData from 'utilities/metadata';

import defaultAvatar from 'assets/images/default-avatar.jpg';
import { ModifiedAddressFormItem } from 'components/address/modifiedAddressFormItem';
import { ModifiedAppInstanceSelectCard } from 'components/app-setup/ModifiedAppInstanceSelectCard';
import { AppInstanceCreateModal, OrgCreateModal } from 'components/modal';
import { ModifiedOrgSelectCard } from 'components/organization';
import { RiBuilding2Line } from 'react-icons/ri';
import './styles.module.css';

export const CreateCompanyConfig: React.FC = () => {
  const [companyId, setCompanyId] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isOrgCreateModalOpen, setIsOrgCreateModalOpen] = useState<boolean>(false);
  const [avatar, setAvatar] = useState<string>();
  const [isSubmitButtonLoading, setIsSubmitButtonLoading] = useState<boolean>(false);
  const [activeTabKey, setActiveTabKey] = useState<string>('company_info');
  const vatRateEnabled = useFeatureFlag('sale.vatRate');
  const invoiceProfilesEnabled = useFeatureFlag('sale.invoiceProfiles');
  const [requireApproval, setRequireApproval] = useState<boolean>(false);
  const [approvalToggleLoading, setApprovalToggleLoading] = useState<boolean>(false);
  const { appInstances, setAppInstances, setCurrentAppInstance } = useAppInstanceStore();
  const { organizations, currentOrganization, setOrganizations, setCurrentOrganization } =
    useOrganizationStore();
  const { user } = useAuth0();
  const userData = useUserData();

  const [form] = Form.useForm();

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const data = await getAllCompanyConfig();
        const configId = data[0].id;
        setCompanyId(configId);
        const companyInfo = await getCompanyInfo(configId);
        form.setFieldsValue({
          companyName: companyInfo.companyName,
          companyEmail: companyInfo.companyEmail,
          companyNumber: companyInfo.companyNumber,
          companyWebsite: companyInfo.companyWebsite,
          address: companyInfo.address,
          tax: companyInfo.companyVatNo,
          bin: companyInfo.companyBinNo,
        });
        setAvatar(companyInfo.companyConfigPhoto[0]?.avatar?.url);
      } catch (err) {
        notification.error({
          message: 'Failed',
          description: 'Failed to load company info',
        });
      }
      setIsLoading(false);
    })();
  }, [form]);

  const fetchOrgAndAppInstance = async () => {
    setIsLoading(true);
    try {
      const [organizationResponse, appInstanceResponse] = await Promise.all([
        getOrganizations(),
        getAppInstances(),
      ]);
      setOrganizations(organizationResponse);
      setAppInstances(appInstanceResponse);
    } catch (err: any) {
      console.error(err.message);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (!user) return;
    fetchOrgAndAppInstance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (userData.user) {
      const tokenOrgId = userData.user.organization.id;
      const tokenAppId = userData.user.platform.id;

      const currentOrganization = organizations.find((org) => org.id === tokenOrgId);
      if (currentOrganization) {
        setCurrentOrganization(currentOrganization);
      }

      const currentAppInstance = appInstances.find((app) => app.id === tokenAppId);
      if (currentAppInstance) {
        setCurrentAppInstance(currentAppInstance);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizations, appInstances, userData.user]);

  useEffect(() => {
    if (userData.user) {
      const tokenOrgId = userData.user.organization.id;
      const tokenAppId = userData.user.platform.id;

      const currentOrganization = organizations.find((org) => org.id === tokenOrgId);
      if (currentOrganization) {
        setCurrentOrganization(currentOrganization);
      }

      const currentAppInstance = appInstances.find((app) => app.id === tokenAppId);
      if (currentAppInstance) {
        setCurrentAppInstance(currentAppInstance);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizations, appInstances, userData.user]);

  const onFinish = async (values: any) => {
    setIsSubmitButtonLoading(true);
    try {
      const data = {
        companyName: values.companyName,
        companyEmail: values.companyEmail,
        companyNumber: values.companyNumber,
        companyWebsite: values.companyWebsite,
        address: values.address,
        avatar: [] as any,
        companyVatNo: values.tax,
        companyBinNo: values.bin,
      };

      const { avatar } = values;

      if (avatar && avatar.length) {
        const file = avatar[0];
        const format = file.type || '';
        const size = file.size || 0;

        if (
          isJpgOrPngFile(format, file.name || '') &&
          isLessThanSize(size, MAX_UPLOAD_FILE_SIZE_MB)
        ) {
          if (file.originFileObj) {
            const base64String = await file2Base64(file);
            const { originFileObj, ...rest } = file;
            data.avatar.push({ ...rest, base64String });
          } else {
            data.avatar.push(file);
          }
        }
      }

      const updatedCompanyInfo = await updateCompanyInfo(companyId!, data);
      form.setFieldsValue({
        companyName: updatedCompanyInfo.companyName,
        companyEmail: updatedCompanyInfo.companyEmail,
        companyNumber: updatedCompanyInfo.companyNumber,
        companyWebsite: updatedCompanyInfo.companyWebsite,
        address: updatedCompanyInfo.address,
        tax: updatedCompanyInfo.companyVatNo,
        bin: updatedCompanyInfo.companyBinNo,
      });
      form.resetFields(['avatar']);
      setAvatar(updatedCompanyInfo.companyConfigPhoto[0]?.avatar?.url);
      notification.success({
        message: 'Success',
        description: 'Successfully updated.',
      });
    } catch (err) {
      notification.error({
        message: 'Failed',
        description: 'Failed to update company info.',
      });
    }
    setIsSubmitButtonLoading(false);
  };

  const CustomTab: React.FC<{ icon: React.ReactNode; text: string }> = ({ icon, text }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
      {icon}
      <span>{text}</span>
    </div>
  );

  const tabListNoTitle = [
    {
      key: 'company_info',
      tab: <CustomTab icon={<RiBuilding2Line />} text="Company Information" />,
    },
    {
      key: 'bank_accounts',
      tab: <CustomTab icon={<Icons.BankOutlined />} text="Bank Accounts" />,
    },
    {
      key: 'company_concerns',
      tab: <CustomTab icon={<Icons.BranchesOutlined />} text="Companies & Concerns" />,
    },
    ...(invoiceProfilesEnabled
      ? [{ key: 'invoices', tab: <CustomTab icon={<Icons.FileTextOutlined />} text="Invoices" /> }]
      : []),
  ];

  const loadApprovalSetting = async () => {
    try {
      const value = await getErpOption('requireStockTransferApproval');
      setRequireApproval(value === 'true');
    } catch {
      setRequireApproval(false);
    }
  };

  useEffect(() => {
    if (activeTabKey === 'company_concerns') {
      loadApprovalSetting();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabKey]);

  const handleApprovalToggle = async (checked: boolean) => {
    setApprovalToggleLoading(true);
    try {
      await setErpOption('requireStockTransferApproval', checked ? 'true' : 'false');
      setRequireApproval(checked);
      notification.success({
        message: 'Success',
        description: `Stock transfer approval ${checked ? 'enabled' : 'disabled'}`,
      });
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err?.message ?? 'Failed to save setting',
      });
    } finally {
      setApprovalToggleLoading(false);
    }
  };

  const onTab1Change = (key: string) => {
    setActiveTabKey(key);
  };

  const headerButton = (id: string, displayName: string) => {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <CreateButton type="primary" onClick={() => setIsCreateModalOpen(true)}>
          Add Concerns
        </CreateButton>

        <ModifiedOrgSelectCard id={id} name={displayName} isHeader={true} />
      </div>
    );
  };

  return (
    <>
      <MetaData />
      <Show title="Settings" headerProps={{ extra: null }} isLoading={isLoading}>
        <Card
          style={{ width: '100%' }}
          tabList={tabListNoTitle}
          activeTabKey={activeTabKey}
          onTabChange={(key) => {
            onTab1Change(key);
          }}
          bodyStyle={{ display: 'none' }}
        ></Card>
        <Space direction="vertical" size="middle" style={{ display: 'flex', marginTop: 20 }}>
          {activeTabKey === 'company_info' && (
            <Card
              title="Company Information"
              size="small"
              id="company_info"
              headStyle={{ fontSize: '16px', padding: '16px 24px 16px 24px', fontWeight: 'bold' }}
            >
              <Form layout="vertical" form={form} onFinish={onFinish} style={{ padding: '0 24px' }}>
                <Row gutter={[32, 40]}>
                  <Col span={12}>
                    <Space
                      direction="vertical"
                      style={{
                        width: '100%',
                        textAlign: 'start',
                        marginBottom: '16px',
                        marginTop: '6px',
                      }}
                    >
                      <Typography.Text
                        style={{
                          fontWeight: 'bold',
                          color: 'rgb(97,97,97)',
                          fontSize: '14px',
                          lineHeight: '22px',
                        }}
                      >
                        <span
                          style={{ color: 'red', fontSize: '21px', position: 'relative', top: 7 }}
                        >
                          *
                        </span>{' '}
                        Company Logo
                      </Typography.Text>
                      <Form.Item
                        name="avatar"
                        valuePropName="fileList"
                        getValueFromEvent={getValueFromEvent}
                        noStyle
                      >
                        <Upload.Dragger
                          listType="picture"
                          accept="image/png, image/jpeg, image/heic, image/heif, .heic, .heif"
                          maxCount={1}
                          beforeUpload={beforeImageUploadValidation}
                        >
                          <Avatar
                            size={80}
                            style={{ objectFit: 'cover' }}
                            src={avatar || defaultAvatar}
                            alt="Store Location"
                          />
                          <p className="ant-upload-text">Drag & drop an image file in this area</p>
                        </Upload.Dragger>
                      </Form.Item>
                    </Space>
                  </Col>
                </Row>

                <Row gutter={[32, 40]}>
                  <Col span={12}>
                    <Form.Item
                      label="Company Name"
                      name="companyName"
                      rules={[
                        {
                          required: true,
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Email"
                      name="companyEmail"
                      rules={[
                        {
                          type: 'email',
                          required: true,
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[32, 40]}>
                  <Col span={12}>
                    <Form.Item
                      label="Mobile Number"
                      name="companyNumber"
                      rules={[
                        {
                          // pattern: /^(\+\d{1,3}[- ]?)?\d{8,12}$/,
                          message: 'Provide valid number',
                          required: true,
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Website" name="companyWebsite">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[32, 40]}>
                  <Col span={12}>
                    <Form.Item
                      label="BIN"
                      name="bin"
                      rules={[
                        {
                          pattern: /^[^(A-z),%'`";*^!._@#$=~?()/\\<>]+$/g,
                          message: 'Only numbers and - are acceptable',
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="TAX"
                      name="tax"
                      rules={[
                        {
                          pattern: /^[^,%'`";*^!_+@#$=~?/\\<>]+$/g,
                          message: 'Only alphabets, numbers, .-&() are acceptable',
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <ModifiedAddressFormItem />
                <Row gutter={[32, 40]}>
                  <Col span={24} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Form.Item wrapperCol={{ span: 4 }}>
                      <Button
                        type="primary"
                        htmlType="submit"
                        icon={<Icons.SaveOutlined />}
                        loading={isSubmitButtonLoading}
                      >
                        Save
                      </Button>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Card>
          )}
          {activeTabKey === 'invoices' && invoiceProfilesEnabled && <InvoiceProfilesSettingCard />}
          {activeTabKey === 'bank_accounts' && (
            <Card size="small" id="bank_accounts">
              {companyId && <PaymentMethodsTable resource="company" resourceId={companyId} />}
            </Card>
          )}

          {activeTabKey === 'company_concerns' && (
            <>
              <Card
                title={currentOrganization?.displayName}
                size="small"
                id="company_concerns"
                headStyle={{
                  fontSize: '16px',
                  padding: '16px 24px 16px 24px',
                  fontWeight: 'bold',
                }}
                extra={
                  currentOrganization &&
                  headerButton(currentOrganization.id, currentOrganization.displayName)
                }
              >
                <div style={{ padding: '24px' }}>
                  <OrgCreateModal
                    isModalOpen={isOrgCreateModalOpen}
                    closeModal={() => setIsOrgCreateModalOpen(false)}
                  />

                  <AppInstanceCreateModal
                    isModalOpen={isCreateModalOpen}
                    closeModal={() => setIsCreateModalOpen(false)}
                  />

                  <Row gutter={[12, 12]}>
                    {appInstances.map((app) => (
                      <Col key={app.id}>
                        <ModifiedAppInstanceSelectCard id={app.id} name={app.displayName} />
                      </Col>
                    ))}
                  </Row>
                </div>
              </Card>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  padding: '16px 24px',
                }}
              >
                <CreateButton
                  type="primary"
                  title="Add Company"
                  onClick={() => setIsOrgCreateModalOpen(true)}
                >
                  Add Company
                </CreateButton>
              </div>

              <Card
                title="Stock Transfer Settings"
                size="small"
                style={{ marginTop: 16 }}
                headStyle={{ fontSize: '16px', padding: '16px 24px', fontWeight: 'bold' }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 0',
                  }}
                >
                  <div>
                    <Typography.Text strong>
                      Require main-warehouse approval for stock transfers
                    </Typography.Text>
                    <div>
                      <Typography.Text type="secondary">
                        New transfers start as Pending Approval and must be approved before
                        delivery. Applies to this concern only.
                      </Typography.Text>
                    </div>
                  </div>
                  <Switch
                    checked={requireApproval}
                    loading={approvalToggleLoading}
                    onChange={handleApprovalToggle}
                  />
                </div>
              </Card>
              {vatRateEnabled && <VatRateSettingCard />}
            </>
          )}
        </Space>
      </Show>
    </>
  );
};
